import { Stack, Chip, Button, Box, Card, CardContent, Typography, CircularProgress } from '@mui/material'
import { FotosProducto, IProducto } from '../types/producto'
import NoResults from '../../../components/Common/NoResults'
import DetalleFotos from './detalleFotos'
import { PaginatorRes } from '../../Pedidos/context/pedidos.context'
import TablaServerPagination from '../../../components/Common/utils/paginator/serverPaginator'

export interface ProductsImgProps {
  items: IProducto[]
  paginator: PaginatorRes
  contadorEstado: (fotos: FotosProducto[]) => {
    pendiente: number
    caption: number
    error: number
  }
  loadingButtons: Record<string, boolean>
  loading: boolean
  sincronizarFotos: (limit: number, idFoto: number, idProducto: number) => Promise<void>
  eliminarCaptions: (idFoto: number, idProducto: number) => void
}

const HEADERS = [
  { nombre: 'Thumbnail', key: 'urlThumb' },
  { nombre: 'Cod. Producto', key: 'codProducto' },
  { nombre: 'Producto', key: 'nombre' },
  { nombre: 'Local', key: 'nombreLocal' },
  { nombre: 'Rubro', key: 'nombreRubro' },
  { nombre: 'Estado', key: 'estado' },
  { nombre: '', key: '' }
]

const FotosDesktop = ({
  items,
  paginator,
  contadorEstado,
  loadingButtons,
  loading,
  sincronizarFotos,
  eliminarCaptions
}: ProductsImgProps) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      {items.length === 0 ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider' }}>
          <NoResults />
        </Box>
      ) : (
        items.map((p) => {
          const contadores = contadorEstado(p.fotosBuscadorImagenes || [])
          const haySinCaption = p.fotosBuscadorImagenes?.some((foto) => foto.caption === null)
          const hayAlgunaConCaption = p.fotosBuscadorImagenes?.some((foto) => foto.caption !== null)
          return (
            <Card
              key={p.idProducto}
              sx={{
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              {/* ENCABEZADO: Foto + Info + Botones */}
              <Box sx={{ display: 'flex' }}>
                {/* Datos a la derecha */}
                <CardContent sx={{ flex: 1, py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                    {/* Información del producto */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {`${p.nombre} (${p.codProducto})`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Local:</strong> {`${p.nombreLocal} (${p.codLocal})`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Rubro:</strong> {p.nombreRubro}
                      </Typography>
                    </Box>

                    {/* Chips de estado y botones alineados a la derecha */}
                    <Stack direction="row" spacing={1} sx={{ minWidth: 'fit-content', mt: '0.5rem' }}>
                      {contadores.pendiente > 0 && (
                        <Chip label={`Pendiente (${contadores.pendiente})`} color="warning" size="small" />
                      )}
                      {contadores.caption > 0 && (
                        <Chip label={`Procesada (${contadores.caption})`} color="success" size="small" />
                      )}
                      {contadores.error > 0 && (
                        <Chip label={`Error (${contadores.error})`} color="error" size="small" />
                      )}
                      {p.fotosBuscadorImagenes?.length > 0 && (
                        <>
                          {haySinCaption && (
                            <Button
                              onClick={() => sincronizarFotos(1000, 0, p.idProducto)}
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={loadingButtons[`caption-producto-${p.idProducto}`] || loading}
                            >
                              {loadingButtons[`caption-producto-${p.idProducto}`] ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                'generar caption producto'
                              )}
                            </Button>
                          )}
                          {hayAlgunaConCaption && (
                            <Button
                              onClick={() => eliminarCaptions(0, p.idProducto)}
                              variant="contained"
                              color="error"
                              size="small"
                              disabled={loadingButtons[`eliminar-producto-${p.idProducto}`] || loading}
                            >
                              {loadingButtons[`eliminar-producto-${p.idProducto}`] ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                'eliminar captions producto'
                              )}
                            </Button>
                          )}
                        </>
                      )}
                    </Stack>
                  </Box>
                </CardContent>
              </Box>
              {p.fotosBuscadorImagenes && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                  <DetalleFotos
                    fotos={p.fotosBuscadorImagenes}
                    loading={loading}
                    loadingButtons={loadingButtons}
                    sincronizarFotos={sincronizarFotos}
                    eliminarCaptions={eliminarCaptions}
                  />
                </Box>
              )}
            </Card>
          )
        })
      )}
      <TablaServerPagination component={'td'} label="Productos por página" paginator={paginator} />
    </Box>
  )
}

export default FotosDesktop
